import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Role constants
const ADMIN_ROLES = ['front_office_staff', 'team_admin'];

// Role-based dashboard routes — League Admin is the consolidated
// admin area, so team_admin lands directly on /league-admin/prospects.
const ROLE_DASHBOARDS = {
  front_office_staff: '/league-admin',
  team_admin: '/league-admin/prospects',
  prospect: '/prospect',
  team_active_athlete: '/athlete',
  member: '/member', // MEMBER-FREE-ACCESS-1
};

// Phase 7M — Security hardening.
// Auth tokens are NO LONGER stored in a custom `localStorage.token` key.
// Instead we rely on Supabase's recommended SDK session handling (the SDK
// manages persistence + auto-refresh under its own `sb-<project>-auth-token`
// key, which is opaque to our app code). Token lives in React state for the
// current render tree and is injected into axios only on demand.
const applyAxiosAuth = (accessToken) => {
  if (accessToken) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  // Used to suppress double-fetches of /auth/me when the SDK emits
  // TOKEN_REFRESHED events in quick succession.
  const hydratingRef = useRef(false);

  // Fetch profile from our backend using the current token.
  // Clears state on 401 so stale sessions don't crash the app.
  const fetchUser = useCallback(async (accessToken) => {
    if (!accessToken) {
      setUser(null);
      return null;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setUser(response.data);
      return response.data;
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const isDeactivated =
        status === 403 &&
        (detail?.code === 'account_deactivated' ||
          (typeof detail === 'string' && detail.toLowerCase().includes('deactivated')));

      // Phase 7AA — if the backend just told us this account is
      // deactivated mid-session, surface that on the user object so the
      // ProtectedRoute can redirect cleanly instead of looking unauth'd.
      if (isDeactivated) {
        setUser({
          id: null,
          email: null,
          name: null,
          role: 'deactivated',
          account_status: detail?.account_status || 'deactivated',
        });
        return null;
      }

      // Only treat a 401 as stale session. Transient 5xx (Supabase hiccup,
      // cold start) should NOT force a logout — leave state as-is and let
      // the next call recover.
      if (status !== 401) {
        return null;
      }
      // Phase 7M.1 — one refresh + retry before invalidating.
      // A 401 from /auth/me can simply mean the access_token aged out
      // just before the SDK rotated it (clock skew, cold-start race,
      // tab backgrounded through expiry). Try refresh_token exchange
      // once; only if that retry also 401s is the session truly stale.
      try {
        const { data: refreshData, error: refreshErr } =
          await supabase.auth.refreshSession();
        const refreshedToken = refreshData?.session?.access_token || null;
        if (!refreshErr && refreshedToken) {
          try {
            const retry = await axios.get(`${API}/auth/me`, {
              headers: { Authorization: `Bearer ${refreshedToken}` },
            });
            setToken(refreshedToken);
            applyAxiosAuth(refreshedToken);
            setUser(retry.data);
            return retry.data;
          } catch (retryErr) {
            // Non-401 on retry → leave state as-is; next call recovers.
            if (retryErr?.response?.status !== 401) {
              return null;
            }
            // 401 again → fall through to full logout below.
          }
        }
      } catch {
        /* refresh failure → treat as unauthenticated below */
      }
      // Stale / invalid session — clear everything and sign out of the SDK too.
      setUser(null);
      setToken(null);
      applyAxiosAuth(null);
      try { await supabase.auth.signOut(); } catch { /* ignore */ }
      return null;
    }
  }, []);

  // Bootstrap: read any existing Supabase-managed session on first mount.
  // This replaces the old `localStorage.getItem('token')` init path.
  useEffect(() => {
    // Phase 8.4.2 — global axios interceptor for Staff Preview write
    // protection. When the backend rejects a live member action because
    // the caller is an admin role (`staff_preview_blocked`), surface a
    // consistent toast so any page that triggers a write gets the same
    // UX without each component having to handle the error itself.
    const interceptorId = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const detail = err?.response?.data?.detail;
        const code = typeof detail === 'object' ? detail?.code : null;
        if (err?.response?.status === 403 && code === 'staff_preview_blocked') {
          const msg =
            (typeof detail === 'object' && detail?.message) ||
            'Staff Preview Mode — action disabled.';
          toast.error(msg, { id: 'staff-preview-blocked', duration: 4000 });
        }
        return Promise.reject(err);
      },
    );

    let cancelled = false;
    // One-time migration cleanup — purge the legacy custom `token` key from
    // any existing user's localStorage. Keeping it around leaves a stale
    // plaintext token that XSS could still read until the user signs out.
    try { window.localStorage?.removeItem('token'); } catch { /* ignore */ }
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const session = data?.session || null;
        const accessToken = session?.access_token || null;
        if (cancelled) return;
        if (accessToken) {
          setToken(accessToken);
          applyAxiosAuth(accessToken);
          await fetchUser(accessToken);
        }
      } catch {
        /* ignore — treat as unauthenticated */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    // Subscribe to session changes from the Supabase SDK. Covers:
    // - TOKEN_REFRESHED (SDK auto-refresh via the refresh_token)
    // - SIGNED_OUT (any tab signing out)
    // - SIGNED_IN (another tab signing in)
    const { data: sub } = supabase.auth.onAuthStateChange(async (event, session) => {
      const nextToken = session?.access_token || null;
      setToken(nextToken);
      applyAxiosAuth(nextToken);
      if (event === 'SIGNED_OUT' || !nextToken) {
        setUser(null);
        return;
      }
      // Avoid a thundering herd of /auth/me calls if we're mid-hydrate.
      if (hydratingRef.current) return;
      hydratingRef.current = true;
      try {
        await fetchUser(nextToken);
      } finally {
        hydratingRef.current = false;
      }
    });

    return () => {
      cancelled = true;
      sub?.subscription?.unsubscribe?.();
      // Phase 8.4.2 — tear down the staff-preview axios interceptor on unmount.
      axios.interceptors.response.eject(interceptorId);
    };
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, refresh_token, user: userData } = response.data;
    // Persist via Supabase SDK (its recommended session store). The SDK will
    // also auto-refresh the access_token before expiry.
    // Phase 7M.1 — pass the real refresh_token only. Never fall back to the
    // access_token (that produced an invalid refresh pair and prevented the
    // SDK from auto-rotating sessions, causing spurious 401 logouts).
    if (access_token && refresh_token) {
      try {
        await supabase.auth.setSession({ access_token, refresh_token });
      } catch { /* SDK session sync is best-effort */ }
    }
    setToken(access_token);
    setUser(userData);
    applyAxiosAuth(access_token);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, refresh_token, user: newUser } = response.data;
    if (access_token && refresh_token) {
      try {
        await supabase.auth.setSession({ access_token, refresh_token });
      } catch { /* SDK session sync is best-effort */ }
    }
    setToken(access_token);
    setUser(newUser);
    applyAxiosAuth(access_token);
    return newUser;
  };

  const logout = async () => {
    setToken(null);
    setUser(null);
    applyAxiosAuth(null);
    try { await supabase.auth.signOut(); } catch { /* ignore */ }
  };

  const isAuthenticated = !!user;
  const isMember = user?.role === 'member';
  const isProspect = user?.role === 'prospect';
  const isTeamActiveAthlete = user?.role === 'team_active_athlete';
  const isTeamAdmin = user?.role === 'team_admin';
  const isFrontOffice = user?.role === 'front_office_staff';
  const isAdminRole = ADMIN_ROLES.includes(user?.role);

  // Legacy compat helpers (used in some components)
  const isAdmin = isAdminRole;
  const isAthlete = isProspect || isTeamActiveAthlete;
  const isFan = isMember;

  // Trial logic
  const trialEndsAt = user?.trial_ends_at ? new Date(user.trial_ends_at) : null;
  const isTrialActive = trialEndsAt ? trialEndsAt > new Date() : false;
  const trialDaysLeft = isTrialActive ? Math.max(0, Math.ceil((trialEndsAt - new Date()) / (1000 * 60 * 60 * 24))) : 0;
  const trialUsed = !!user?.trial_ends_at;

  // MEMBER-FREE-ACCESS-1 (Feb 2026) — every registered `role='member'`
  // user receives full access to the core Federation Sports member
  // experience immediately after registration + team selection. The
  // previous gating (paid `membership='pro'` or active trial) is
  // removed for member participation features. Pro flag still flips
  // true for admins (admin-staff preview) and active trialists so
  // legacy Pro-only surfaces in Draft Pool, Draft Chat, and Navbar
  // continue to work without ad-hoc patches across the codebase.
  const isPro = user?.role === 'member' || user?.membership === 'pro' || isAdminRole || isTrialActive;

  // Get dashboard route for current user
  const getDashboardRoute = () => ROLE_DASHBOARDS[user?.role] || '/';

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      isAuthenticated,
      // New unified role checks
      isMember,
      isProspect,
      isTeamActiveAthlete,
      isTeamAdmin,
      isFrontOffice,
      isAdminRole,
      // Legacy compat
      isAdmin,
      isAthlete,
      isFan,
      // Pro / trial
      isPro,
      isTrialActive,
      trialDaysLeft,
      trialUsed,
      // Helpers
      getDashboardRoute,
      fetchUser: () => fetchUser(token),
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
